import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { Save, Maximize2, Minimize2, RotateCcw } from 'lucide-react';

interface MonacoCodeEditorProps {
  value: string;
  language?: string;
  filename?: string;
  onSave?: (newValue: string) => Promise<void> | void;
  readOnly?: boolean;
}

export const MonacoCodeEditor: React.FC<MonacoCodeEditorProps> = ({
  value,
  language = 'lua',
  filename,
  onSave,
  readOnly = false,
}) => {
  const [content, setContent] = useState(value);
  const [isDirty, setIsDirty] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setContent(value);
    setIsDirty(false);
  }, [value]);

  // Determine language mode based on filename extension
  const getLanguage = () => {
    if (language) return language;
    if (!filename) return 'plaintext';
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'lua':
        return 'lua';
      case 'toml':
        return 'ini'; // Monaco highlights TOML well with ini or custom
      case 'json':
        return 'json';
      case 'yaml':
      case 'yml':
        return 'yaml';
      case 'md':
        return 'markdown';
      case 'xml':
        return 'xml';
      default:
        return 'plaintext';
    }
  };

  const handleSave = async () => {
    if (!onSave || !isDirty) return;
    setIsSaving(true);
    try {
      await onSave(content);
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to save file:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Keyboard shortcut Ctrl+S / Cmd+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [content, isDirty]);

  return (
    <div
      className={`flex flex-col border border-panel-border rounded-xl overflow-hidden bg-panel-card transition-all ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'h-[600px] w-full'
      }`}
    >
      {/* Editor Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-panel-subtle border-b border-panel-border text-xs font-mono">
        <div className="flex items-center gap-2">
          <span className="text-neutral-300 font-semibold">{filename || 'Untitled'}</span>
          <span className="px-2 py-0.5 rounded bg-panel-border text-neutral-400 uppercase text-[10px]">
            {getLanguage()}
          </span>
          {isDirty && (
            <span className="px-2 py-0.5 rounded bg-amber-950/70 border border-amber-800/80 text-amber-300 text-[10px]">
              Unsaved Changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={() => {
                setContent(value);
                setIsDirty(false);
              }}
              title="Discard changes"
              className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}

          {!readOnly && onSave && (
            <button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-sans font-medium transition ${
                isDirty
                  ? 'bg-brand-600 hover:bg-brand-500 text-white shadow-sm'
                  : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
              }`}
            >
              <Save className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Saving...' : 'Save (Ctrl+S)'}</span>
            </button>
          )}

          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            className="p-1.5 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition ml-2"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Monaco Container */}
      <div className="flex-1 w-full overflow-hidden bg-[#1e1e1e]">
        <Editor
          height="100%"
          language={getLanguage()}
          value={content}
          theme="vs-dark"
          options={{
            readOnly,
            fontSize: 13,
            fontFamily: "'JetBrains Mono', Consolas, monospace",
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            lineNumbers: 'on',
            tabSize: 2,
            wordWrap: 'on',
          }}
          onChange={(newVal) => {
            setContent(newVal || '');
            setIsDirty(newVal !== value);
          }}
        />
      </div>
    </div>
  );
};
